/**
 * @prettier
 */

// React Packages
import { useRef, useState, useEffect } from "react";
import { connect } from "react-redux";
import { useTranslation, Trans } from "react-i18next";

// Services
import selectStorage from "../../services/shared/selectStorage";
import { areCookiesEnabled, isInIframe } from "../../services/shared/helpers";
import DataDogService from "../../services/shared/datadogService";
import { clearUserPII } from "../../services/shared/submissionCompletion";

// Other packages
import QRCode from "react-qr-code";

// Components
import Header from "../../components/Header";
import { ChromeIcon, SafariIcon } from "./components/Icons";
import ShowRedirectURLState from "./components/ShowRedirectURLState";

import { LoadingStateAnimation } from "../../components/ProcessingLayout";

function getBackgroundColor(hex) {
    var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
        return r + r + g + g + b + b;
    });

    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)},${parseInt(result[3], 16)},0.3)`;
}

const MobileHandoff = ({ pendingItems, branding, returnUrl, whitelabel, redirectTimeout }) => {
    const { t } = useTranslation();
    const primaryFocusRef = useRef(null);
    const revealCompletedViewTimerRef = useRef(null);
    const [uuid, setUuid] = useState("");
    const [showRedirectURLContinueButton, setShowRedirectURLContinueButton] =
        useState(false);
    const [showCustomClientsLogo, setShowCustomClientsLogo] = useState(false);
    const brandingColor = `#${branding.data.company_branding.primary_color}`;
    const customClientsLogo = branding?.logo || null;

    useEffect(() => {
        setUuid(selectStorage(areCookiesEnabled()).getItem("uuid"));
        document.title = t("idpal_doc_title_handoff");
        if (primaryFocusRef && primaryFocusRef.current) {
            primaryFocusRef.current.focus();
        }

        if (customClientsLogo && !isInIframe()) {
            setShowCustomClientsLogo(true);
        }
    }, [t, customClientsLogo]);

    // Reveal the "completed on mobile" flow after a delay. When the user
    // returns to the QR screen, showRedirectURLContinueButton goes false
    // and this effect runs again so the delay restarts from zero.
    useEffect(() => {
        if (!returnUrl || showRedirectURLContinueButton) {
            return undefined;
        }
        if (revealCompletedViewTimerRef.current) {
            clearTimeout(revealCompletedViewTimerRef.current);
        }
        revealCompletedViewTimerRef.current = setTimeout(() => {
            revealCompletedViewTimerRef.current = null;
            setShowRedirectURLContinueButton(true);
        }, redirectTimeout);
        return () => {
            if (revealCompletedViewTimerRef.current) {
                clearTimeout(revealCompletedViewTimerRef.current);
                revealCompletedViewTimerRef.current = null;
            }
        };
    }, [returnUrl, showRedirectURLContinueButton]);

    const handleCompletedOnMobileClick = () => {
        if (revealCompletedViewTimerRef.current) {
            clearTimeout(revealCompletedViewTimerRef.current);
            revealCompletedViewTimerRef.current = null;
        }
        DataDogService.log(
            "User confirmed mobile handoff completion, redirecting to return_url."
        );
        clearUserPII();
        window.location.replace(returnUrl);
    };

    const handleReturnToQrCodeClick = () => {
        setShowRedirectURLContinueButton(false);
    };

    const buildHandoffUrl = () => {
        return (
            window.location.protocol +
            "//" +
            window.location.host +
            "?uuid=" +
            uuid +
            "&handoff_session=" +
            selectStorage(areCookiesEnabled()).getItem("sessionid") +
            "&lang=" +
            selectStorage(areCookiesEnabled()).getItem("lang") +
            "&target=webapp_mobile"
        );
    };
    if (pendingItems > 0) {
        return (
            <div className="u-text-center start-loading">
                <LoadingStateAnimation />
            </div>
        );
    }

    if (showRedirectURLContinueButton && returnUrl) {
        return (
            <ShowRedirectURLState
                showCustomClientsLogo={showCustomClientsLogo}
                onContinueClick={handleCompletedOnMobileClick}
                onReturnToQrClick={handleReturnToQrCodeClick}
            />
        );
    }

    return (
        <div className="root-desktop-layout">
            <Header showLanguageSelect />
            <section
                className="info-box-wrapper"
                style={{ backgroundColor: getBackgroundColor(brandingColor) }}
            >
                <h1 className="info-box-title">
                    {t("idpal_mobille_handoff_main_title")}
                </h1>
                <p className="info-box-secondary-text">
                    {t("idpal_mobille_handoff_main_subtitle")}
                </p>
                <div className="info-box-scanning-instructions-wrapper">
                    <QRCode
                        className="qr-code-img"
                        value={buildHandoffUrl()}
                        size={200}
                    />

                    <ol className="scanning-instructions-list">
                        <span className="list-header">
                            {t("idpal_mobille_handoff_instructions_title")}
                        </span>
                        <li>{t("idpal_mobille_handoff_instructions_step1")}</li>
                        <li>{t("idpal_mobille_handoff_instructions_step2")}</li>
                        <li>
                            <Trans
                                i18nKey="idpal_mobille_handoff_instructions_step3"
                                components={{
                                    chromeIcon: <ChromeIcon />,
                                    safariIcon: <SafariIcon />,
                                }}
                            />
                        </li>
                    </ol>
                </div>
            </section>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        branding: state.config.profile,
        whitelabel: state.config.profile.data.company_branding.whitelabel,
        uuidValid: state.config.uuidValid,
        pendingItems: state.spinner.pendingItems,
        returnUrl: state.config.returnUrl,
        redirectTimeout: state.config.redirectTimeout,
    };
}

export default connect(mapStateToProps)(MobileHandoff);
